import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OrderAccessGroupResponse } from '../../../models/groups/order-access-group-response.model';
import { PaginatorModule } from 'primeng/paginator';
import { HttpParams } from '@angular/common/http';
import { QueryParamsService } from '../../../../core/query-params.service';
import { SortDirection } from '../../orders/table/sort-direction.enum';

@Component({
  selector: 'app-group-table',
  standalone: true,
  imports: [CommonModule, PaginatorModule],
  templateUrl: './group-table.component.html',
  styleUrls: ['./group-table.component.scss'],
})
export class GroupTableComponent implements OnChanges {
  @Input() groups: OrderAccessGroupResponse[] = [];
  @Output() rowClick = new EventEmitter<OrderAccessGroupResponse>();
  @Input() totalPages = 0;

  itemsPerPageOptions = [5, 10, 25, 50, 100];
  itemsPerPageShown = this.itemsPerPageOptions[2];
  first = 0;
  currentPage = 0;
  totalRecords = 0;
  // sorting
  sortColumn = '';
  sortDirection: SortDirection = SortDirection.NONE;
  SortDirection = SortDirection;

  columns: { key: string; label: string; allowSort: boolean; align: 'left' | 'center' }[] = [
    { key: 'NAME', label: 'Name', allowSort: true, align: 'left' },
    { key: 'CUSTOMER', label: 'Kunde', allowSort: true, align: 'left' },
    { key: 'orderTitle', label: 'Aufträge', allowSort: false, align: 'left' },
    { key: 'members', label: 'Mitglieder', allowSort: false, align: 'center' },
  ];

  constructor(private queryService: QueryParamsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    const http = this.queryService.getCurrentQueryParams();
    this.updatePaginationParams(http);
    this.updateSortParams(http);
    if (changes['groups'] && changes['groups'].currentValue) {
      this.totalRecords = this.totalPages * this.itemsPerPageShown;
    }
  }

  onPageChange(event: any) {
    this.first = event.first;
    this.currentPage = event.page;
    this.itemsPerPageShown = event.rows;

    let http: HttpParams = this.queryService.getCurrentQueryParams();
    http = http.set('page', this.currentPage).set('size', this.itemsPerPageShown);
    this.paginationChangeEvent.emit({ params: http });
  }

  @Output() paginationChangeEvent = new EventEmitter<{ params: HttpParams }>();

  getMemberCount(group: any): number {
    if (!group) return 0;
    if (typeof group.members === 'number') return group.members;
    if (typeof group.userCount === 'number') return group.userCount;
    if (Array.isArray(group.members)) return group.members.length;
    if (Array.isArray(group.users)) return group.users.length;
    return 0;
  }

  onRowClick(group: OrderAccessGroupResponse) {
    this.rowClick.emit(group);
  }

  getOrderTitlesDisplay(group: any): string[] {
    if (!group) return [];
    const titles = group.orderTitles;
    if (Array.isArray(titles)) return titles.filter((t: any) => !!t);
    const single = group.orderTitle || group.title;
    if (typeof single === 'string' && single.length > 0) return [single];
    return [];
  }

  setSort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection =
        this.sortDirection === SortDirection.ASC
          ? SortDirection.DESC
          : this.sortDirection === SortDirection.DESC
            ? SortDirection.NONE
            : SortDirection.ASC;
      if (this.sortDirection === SortDirection.NONE) {
        this.sortColumn = '';
      }
    } else {
      this.sortColumn = column;
      this.sortDirection = SortDirection.ASC;
    }

    this.setSortingParams(this.sortColumn, this.sortDirection);
  }

  setSortingParams(sortKey: string, sortDirection: string): void {
    let currentHttpParams = this.queryService.getCurrentQueryParams();
    currentHttpParams =
      sortDirection !== SortDirection.NONE
        ? currentHttpParams.set('sortKey', sortKey).set('sortDirection', sortDirection)
        : currentHttpParams.delete('sortKey').delete('sortDirection');
    this.paginationChangeEvent.emit({ params: currentHttpParams });
  }

  private updateSortParams(httpParams: HttpParams): void {
    const sortKeyParam = httpParams.get('sortKey');
    const sortDirectionParam = httpParams.get('sortDirection');

    this.sortDirection = this.getValidSortDirection(sortDirectionParam);
    this.sortColumn = sortKeyParam || '';
  }

  private getValidSortDirection(sortDirectionParam: string | null): SortDirection {
    return sortDirectionParam && Object.values(SortDirection).includes(sortDirectionParam as SortDirection)
      ? (sortDirectionParam as SortDirection)
      : SortDirection.NONE;
  }
  private updatePaginationParams(httpParams: HttpParams): void {
    const pageValue = httpParams.get('page');
    const shown = httpParams.get('size');
    this.currentPage = Number(pageValue) || 0;
    this.itemsPerPageShown = Number(shown) || this.itemsPerPageShown;
    this.first = this.currentPage * this.itemsPerPageShown;
  }
}
